import { DtSort, DtFilter, Dataset } from '@/components/data-table/helpers';
import { DateRangeValue, downloadCsv, getDaysAgoDate, JsonObject, UiOption } from '../helpers';
import { AuditLogModel } from '../models/audit-log/audit-log.model';
import { axiosClient as spApiClient, get, post } from './sp-api.service';
import { passErrorResponse } from '.';
import { endOfDay, startOfDay } from 'date-fns';
import { AuditLogSettingsModel } from '../models/audit-log/audit-settings.model';

type SearchFilter = {
  keyword?: string;
  actor?: null;
  userId?: string | null;
  categories?: string[];
  actions?: string[];
  resourceId?: string;
  date?: {
    start?: string;
    end?: string;
  };
};

const getFilterParams = (dtFilter?: DtFilter): SearchFilter => {
  const filterParams: SearchFilter = {};

  if (dtFilter) {
    const { keyword, values } = dtFilter;

    if (keyword) {
      filterParams.keyword = keyword.length > 2 ? keyword : undefined;
    }

    values.forEach(({ columnName, value }) => {
      switch (columnName) {
        case 'actor': {
          const typedValue = value as UiOption;
          if (typedValue.value === 'system') {
            filterParams.actor = null;
          } else {
            filterParams.userId = typedValue.value;
          }
          break;
        }
        case 'date':
          {
            const [startTime, endTime] = value as DateRangeValue;
            const startTimestamp = startTime ? startOfDay(startTime).toISOString() : null;
            const endTimestamp = endTime ? endOfDay(endTime).toISOString() : null;

            if (startTimestamp || endTimestamp) {
              const dateFilter: { start?: string; end?: string } = {};

              if (startTimestamp) {
                dateFilter.start = startTimestamp;
              }

              if (endTimestamp) {
                dateFilter.end = endTimestamp;
              }

              filterParams.date = dateFilter;
            }
          }
          break;
        case 'actions':
          filterParams.actions = value as string[];
          break;
        case 'categories':
          filterParams.categories = value as string[];
          break;
        case 'resourceId':
          filterParams.resourceId = value as string;
          break;
      }
    });
  }

  // Max allowed look-back period is 30 days
  const maxLookbackTime = getDaysAgoDate(30).toISOString();

  if (filterParams.date?.start && filterParams.date.start < maxLookbackTime) {
    filterParams.date.start = maxLookbackTime;
  }

  return filterParams;
};

export const getAuditLogs = async (
  page: number,
  pageSize: number,
  _dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<AuditLogModel>> => {
  const filterParams = getFilterParams(dtFilter);

  const { keyword, userId, actor, categories, actions, resourceId, date } = filterParams;

  const result = await post(
    '/audit/audit-events/search',
    {
      page: page + 1,
      pageSize,
      message: keyword,
      userId,
      actor,
      eventType: categories,
      eventAction: actions,
      resourceId,
      startDateTime: date?.start,
      endDateTime: date?.end,
    },
    passErrorResponse
  );

  const { data, count } = result || {};

  if (!result || !Array.isArray(data)) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  const rows: AuditLogModel[] = data.map((row) => new AuditLogModel(row as JsonObject));

  return {
    page,
    pageSize,
    rows,
    total: count as number,
  };
};

export const getAuditLogById = async (id: string): Promise<AuditLogModel | null> => {
  const result = await get(`/audit/audit-events/${id}`);
  return result ? new AuditLogModel(result as JsonObject) : null;
};

export const downloadAuditLogs = async (dtFilter?: DtFilter): Promise<boolean> => {
  const filterParams = getFilterParams(dtFilter);
  const { keyword, userId, actor, categories, actions, resourceId, date } = filterParams;

  const response = await spApiClient
    .post(
      '/audit/audit-events/export',
      {
        message: keyword,
        userId,
        actor,
        eventType: categories,
        eventAction: actions,
        resourceId,
        startDateTime: date?.start,
        endDateTime: date?.end,
      },
      { timeout: 1000 * 60 * 5 }
    )
    .catch((err) => {
      console.error(err);
    });

  if (!response?.data) {
    return false;
  }

  const dateForFilename = new Date().toISOString().replace(/:/g, '-').replace(/\./g, '-');

  downloadCsv(
    response.data as string,
    `f5-workforce-ai-security-audit-logs-export-${dateForFilename}.csv`
  );

  return true;
};

export const getAuditLogSettings = async (): Promise<AuditLogSettingsModel | null> => {
  try {
    const result = await get('/audit/settings');
    return result ? new AuditLogSettingsModel(result as JsonObject) : null;
  } catch (err) {
    console.error(err);
    return null;
  }
};
